import { authorizedFetch, isItemNew } from '../../../utils';
import { getMapping as getQueryParams } from '../../../reporting-utils';

export const PAYMENT_TERMS_OPTIONS = [
  { label: 'Net 30', value: 30 },
  { label: 'Net 45', value: 45 },
  { label: 'Net 60', value: 60 },
  { label: 'Net 90', value: 90 }
];

export const TIME_PERIOD_OPTIONS = [
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Year', value: 'year' }
];

export const FEES_TYPE = {
  SIMPLE: 'simple',
  VARIABLE: 'variable'
};

export const FEES_TYPE_OPTIONS = [
  { label: 'Simple', value: FEES_TYPE.SIMPLE },
  { label: 'Variable on Contingency', value: FEES_TYPE.VARIABLE }
];

export const IMPLEMENTATION_TYPE_OPTIONS = [
  { label: 'Page Overlay', value: 'pageoverlay' },
  { label: 'Embed on Page', value: 'embedonpage' }
];

export const DEFAULT_FEE_ENTRY = {
  amortizationEndDate: '',
  contingentOnSessions: '',
  feePerMonth: '',
  feesType: FEES_TYPE.SIMPLE,
  fixedFee: '',
  implementationType: '',
  clawbackEnabled: false,
  placementExperienceId: '',
  startDate: '',
  timePeriod: 'month'
};

export const AMOUNT_TYPE = {
  DOLLAR: 'dollar',
  PERCENT: 'percent'
};

export const AMOUNT_TYPE_OPTIONS = [
  { label: '$ Amount', value: AMOUNT_TYPE.DOLLAR },
  { label: '% of Revenue', value: AMOUNT_TYPE.PERCENT }
];

export const DEFAULT_REFERRAL_FEE_ENTRY = {
  partnerId: '',
  amountType: AMOUNT_TYPE.DOLLAR,
  amount: '',
  startDate: '',
  endDate: '',
  paymentTerms: ''
};

export const INPUT_CONSTRAINTS = {
  PERCENT_MAX: 100,
  PERCENT_STEP: 0.1,
  DOLLAR_STEP: 1,
  MIN_VALUE: 0
};

// Revshare percentages must be a number between 0 and 100 (decimal precision
// beyond 2 places is rounded rather than rejected by the fields that use this)
export const validateRevSharePercentage = value => {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Enter a valid number.';
  if (numeric < 0 || numeric > 100) {
    return 'Enter a percentage between 0 and 100.';
  }
  return null;
};

export const REVENUE_MINIMUM_TYPE_OPTIONS = [
  { label: 'RPS', value: 'RPS' },
  { label: 'RPT', value: 'RPT' }
];

export const GUARANTEE_TYPE = {
  FLAT: 'flat',
  STAGGERED: 'staggered'
};

export const GUARANTEE_TYPE_OPTIONS = [
  { label: 'Flat', value: GUARANTEE_TYPE.FLAT },
  { label: 'Staggered', value: GUARANTEE_TYPE.STAGGERED }
];

export const DEFAULT_GUARANTEE_TIER = () => ({
  lowerBound: '',
  upperBound: '',
  rpsGuarantee: '',
  isRevshare: false
});

export const hasValue = value => value !== '' && value != null;

export const isRevshareTier = tier =>
  tier?.isRevshare === true || hasValue(tier?.revshare);

export const getGuaranteeTierIssues = orderedTiers => {
  const tierIssues = [];
  const listIssues = [];

  for (let i = 1; i < orderedTiers.length; i++) {
    if (
      hasValue(orderedTiers[i].lowerBound) &&
      hasValue(orderedTiers[i - 1].lowerBound) &&
      Number(orderedTiers[i].lowerBound) <
        Number(orderedTiers[i - 1].lowerBound)
    ) {
      tierIssues.push({
        index: i,
        message:
          'Guarantee tiers must be submitted in ascending order by lower bound.'
      });
      return { tierIssues, listIssues };
    }
  }

  orderedTiers.forEach((tier, index) => {
    const isLast = index === orderedTiers.length - 1;
    const revshareTier = isRevshareTier(tier);

    if (
      hasValue(tier.lowerBound) &&
      !Number.isInteger(Number(tier.lowerBound))
    ) {
      tierIssues.push({
        index,
        message: 'Lower bound must be a whole number.'
      });
    }

    if (
      hasValue(tier.upperBound) &&
      !Number.isInteger(Number(tier.upperBound))
    ) {
      tierIssues.push({
        index,
        message: 'Upper bound must be a whole number.'
      });
    }

    if (
      index === 0 &&
      hasValue(tier.lowerBound) &&
      Number(tier.lowerBound) !== 1
    ) {
      tierIssues.push({
        index,
        message: "The first tier's lower bound must be 1."
      });
    }

    if (
      hasValue(tier.lowerBound) &&
      hasValue(tier.upperBound) &&
      Number(tier.upperBound) <= Number(tier.lowerBound)
    ) {
      tierIssues.push({
        index,
        message: 'Upper bound must be greater than the lower bound.'
      });
    }

    if (!isLast && hasValue(tier.lowerBound) && !hasValue(tier.upperBound)) {
      tierIssues.push({
        index,
        message: 'Upper bound is required for all tiers except the final one.'
      });
    }

    if (index > 0 && hasValue(tier.lowerBound)) {
      const prev = orderedTiers[index - 1];
      if (hasValue(prev.upperBound)) {
        const expectedLowerBound = Number(prev.upperBound) + 1;
        const actualLowerBound = Number(tier.lowerBound);
        if (actualLowerBound > expectedLowerBound) {
          tierIssues.push({
            index,
            message: `Gap: sessions ${expectedLowerBound}-${
              actualLowerBound - 1
            } fall outside any tier.`
          });
        } else if (actualLowerBound < expectedLowerBound) {
          tierIssues.push({
            index,
            message: `Overlap: sessions ${actualLowerBound}-${
              expectedLowerBound - 1
            } are covered by more than one tier.`
          });
        }
      }
    }

    if (revshareTier) {
      if (!isLast) {
        tierIssues.push({
          index,
          message: 'The revshare tier must be the last tier.'
        });
      }
      if (hasValue(tier.rpsGuarantee)) {
        tierIssues.push({
          index,
          message: 'A revshare tier cannot also have an RPS guarantee.'
        });
      }
      if (hasValue(tier.revshare)) {
        const rangeError = validateRevSharePercentage(Number(tier.revshare) * 100);
        if (rangeError) {
          tierIssues.push({ index, message: rangeError });
        }
      }
    }
  });

  const revshareCount = orderedTiers.filter(isRevshareTier).length;
  if (revshareCount > 1) {
    listIssues.push('At most one revshare tier is allowed.');
  }

  return { tierIssues, listIssues };
};

export const DEFAULT_REVENUE_SPLIT_SECTION = {
  adUnitConfigIds: [],
  contingentOnSessions: '',
  contingentOnSessionsCostPerSession: '',
  costPerSession: '',
  partnerDataField: '',
  partnerPercentage: '',
  platformPercentage: '',
  revenueMinimum: '',
  revenueMinimumType: '',
  timePeriod: 'month',
  timePeriodCostPerSession: 'month',
  useAllAdUnitConfigs: true,
  useContingentOnSessions: false,
  useCostPerSessionOverRevShareUntilSessionReached: false,
  usePartnerDataField: false,
  guaranteeType: GUARANTEE_TYPE.FLAT,
  guaranteeTiers: []
};

const ALLOWED_PARTNER_DATA_FIELDS = ['email', 'phone'];

export const fetchPartnerDataFields = callback => {
  getQueryParams(
    qps => {
      const filteredParams = qps
        .map(qp => {
          const qpParts = qp.split('.');
          return {
            field: qpParts[qpParts.length - 1],
            displayName: qpParts[qpParts.length - 1]
          };
        })
        .filter(qp => ALLOWED_PARTNER_DATA_FIELDS.includes(qp.field));

      callback(filteredParams);
    },
    false,
    false
  );
};

export const getContractTermItems = async (
  id,
  setContractTermItems,
  addError
) => {
  if (!isItemNew(id)) {
    try {
      const response = await authorizedFetch(
        `api/trafficpartner/${id}/contractterms`,
        'GET'
      );
      setContractTermItems(response);
    } catch (error) {
      addError('Unable to fetch contract term items', JSON.stringify(error));
    }
  }
};
